import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Alert
} from '@mui/material';
import { ActivityHistoryDocument } from '@/types/activityHistory';
import DataComparison from './DataComparison';

interface RestoreConfirmDialogProps {
  open: boolean;
  history: ActivityHistoryDocument | null;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const RestoreConfirmDialog: React.FC<RestoreConfirmDialogProps> = ({
  open,
  history,
  onConfirm,
  onCancel,
  loading = false
}) => {
  if (!history) return null;

  const { metadata } = history;
  const isUpdate = metadata?.isUpdate;
  const oldData = metadata?.oldData;
  const deletedData = metadata?.deletedData;

  // 데이터 입력 날짜 포맷팅 (YY/MM/DD 형식)
  const formatDataInputDate = (metadata: any) => {
    try {
      const deletedData = metadata?.deletedData;
      const oldData = metadata?.oldData;
      const dataInfo = oldData || deletedData;
      
      // createdTime 필드 확인 (Firebase Timestamp 또는 Date)
      let dateToFormat: Date | null = null;
      
      if (dataInfo?.createdTime) {
        if (dataInfo.createdTime.toDate) {
          // Firebase Timestamp
          dateToFormat = dataInfo.createdTime.toDate();
        } else if (dataInfo.createdTime instanceof Date) {
          // Date 객체
          dateToFormat = dataInfo.createdTime;
        } else {
          // 문자열이나 숫자
          dateToFormat = new Date(dataInfo.createdTime);
        }
      } else if (dataInfo?.createTime) {
        // 기존 createTime 필드도 확인
        dateToFormat = new Date(dataInfo.createTime);
      }
      
      if (dateToFormat && !isNaN(dateToFormat.getTime())) {
        const year = dateToFormat.getFullYear().toString().substring(2);
        const month = (dateToFormat.getMonth() + 1).toString().padStart(2, '0');
        const day = dateToFormat.getDate().toString().padStart(2, '0');
        return `${year}/${month}/${day}`;
      }
    } catch (e) {
      console.error('데이터 입력 날짜 추출 오류:', e);
    }
    return '알 수 없음';
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'var(--bg-base)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        background: 'var(--bg-elev-1)',
        borderBottom: '1px solid var(--border-subtle)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
            {isUpdate ? '데이터 되돌리기' : '데이터 복원'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* 설명 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            {isUpdate 
              ? '현재 수정된 값을 이전 값으로 되돌리시겠습니까?'
              : '다음 데이터를 복원하시겠습니까?'
            }
          </Typography>

          {/* 데이터 입력 날짜 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mb: 2,
            p: 2,
            background: 'var(--bg-elev-1)',
            borderRadius: 2,
            border: '1px solid var(--border-subtle)'
          }}>
            <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
              원본 데이터 입력일: {formatDataInputDate(metadata)}
            </Typography>
          </Box>
        </Box>

        {/* 데이터 비교 (수정인 경우) */}
        {isUpdate && oldData && deletedData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              변경 내용
            </Typography>
            <DataComparison oldData={deletedData} currentData={oldData} />
          </Box>
        )}

        {/* 복원할 데이터 정보 (삭제인 경우) - 간단하게 */}
        {!isUpdate && deletedData && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              background: 'var(--bg-elev-1)', 
              borderRadius: 2, 
              p: 2,
              border: '1px solid var(--border-subtle)'
            }}>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'var(--fg-strong)' }}>
                <strong>{deletedData?.name}</strong> - {formatAmount(deletedData?.money || 0)}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* 안내 메시지 */}
        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          <Typography variant="body2">
            {isUpdate 
              ? '수정된 값에서 이전 값으로 되돌립니다. 이 작업은 되돌릴 수 없습니다.'
              : '기존 데이터가 있으면 수정하고, 없으면 새로 추가됩니다. 이 작업은 되돌릴 수 없습니다.'
            }
          </Typography>
        </Alert>

        {/* 경고 메시지 */}
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 2,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          <Typography variant="body2">
            <strong>주의:</strong> 이 작업은 데이터베이스에 직접 영향을 미치며, 
            복원 후에는 원래 상태로 되돌릴 수 없습니다.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600
          }}
        >
          취소
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            px: 3,
            py: 1,
            fontWeight: 500,
            background: '#6c757d',
            '&:hover': {
              background: '#5a6268',
            },
            '&:disabled': {
              background: 'var(--bg-muted)',
              color: 'var(--fg-secondary)'
            }
          }}
        >
          {loading ? '처리 중...' : (isUpdate ? '되돌리기' : '복원')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 금액 포맷팅 함수
const formatAmount = (amount: number) => {
  return new Intl.NumberFormat('ko-KR').format(amount);
};

export default RestoreConfirmDialog;
